import type { GlobalAiTranscriptMessage } from './global-ai-assistant-transcript';
import { runNarrateProgressStep } from './global-ai-narrate-progress-stream';
import type { TurnStepRequestBody } from './global-ai-turn-step-stream';

/**
 * An event a running client tool can emit during execution.
 *
 * - `status`: display-only, routed to the activity lane. No LLM call.
 * - `narrate`: a checkpoint carrying a `digest` to ground on, turned into one
 *   grounded chat line by the narrate-progress endpoint.
 * - `thinking`: a raw chunk of the model's live reasoning (already natural
 *   language from the generation stream). Accumulated verbatim and surfaced via
 *   the `onThinking` callback as live detail under the running trace step — no
 *   LLM call.
 */
export type ToolProgressEvent =
  | { tier: 'status'; text: string }
  | { tier: 'narrate'; kind: string; digest: unknown }
  | { tier: 'thinking'; textChunk: string };

/** The sink a tool calls to stream feedback. A no-op when no sink is wired. */
export type ToolEmit = (event: ToolProgressEvent) => void;

const RECENT_NARRATION_LIMIT = 3;

// Max cadence for flushing streamed thinking tokens to React. ~64ms ≈ 15
// updates/sec — smooth to read, but a small fraction of the ~40 tokens/sec the
// model streams, so a token burst collapses into one re-render instead of one
// per token.
const THINKING_FLUSH_MS = 64;

/**
 * Build the per-turn progress sink: status routes to the activity lane; narrate
 * events queue and drain through a single sequential worker so chat lines append
 * in order. Best-effort — a failed narrate call drops that line, never throws.
 */
export function createToolProgressSink(params: {
  userGoal: string;
  context?: TurnStepRequestBody['context'];
  signal: AbortSignal;
  newId: () => string;
  appendTranscript: (message: GlobalAiTranscriptMessage) => void;
  updateAssistantMessageText: (id: string, text: string) => void;
  setGenerationStatus: (value: string | null) => void;
  onThinking: (text: string) => void;
}): { emit: ToolEmit; close: () => void } {
  const recentNarration: string[] = [];
  const queue: Array<{ kind: string; digest: unknown }> = [];
  let draining = false;
  // Once closed, no further narration is appended. A tool calls this when it
  // finishes so an in-flight narrate can't land in the transcript AFTER the
  // turn's final model response (chat ordering).
  let closed = false;
  // Streamed model reasoning is accumulated verbatim and surfaced through
  // `onThinking` as live detail under the running trace step.
  let thinkingText = '';

  const drain = async (): Promise<void> => {
    if (draining) return;
    draining = true;
    try {
      while (queue.length > 0 && !params.signal.aborted && !closed) {
        // Burst-coalesce: if the next queued events share this kind, collapse
        // to the most recent (a cluster of identical-kind ticks narrates once).
        let next = queue.shift() as { kind: string; digest: unknown };
        while (queue.length > 0 && queue[0]?.kind === next.kind) {
          next = queue.shift() as { kind: string; digest: unknown };
        }

        const segmentId = params.newId();
        let appended = false;
        let lastText = '';
        try {
          await runNarrateProgressStep({
            signal: params.signal,
            body: {
              userGoal: params.userGoal,
              recentNarration: recentNarration.slice(-RECENT_NARRATION_LIMIT),
              event: next,
              ...(params.context ? { context: params.context } : {}),
            },
            onText: (cumulativeText) => {
              if (params.signal.aborted || closed) return;
              lastText = cumulativeText;
              if (!appended) {
                appended = true;
                params.appendTranscript({
                  id: segmentId,
                  role: 'assistant',
                  kind: 'text',
                  text: cumulativeText,
                  ephemeral: true,
                });
              } else {
                params.updateAssistantMessageText(segmentId, cumulativeText);
              }
            },
          });
        } catch {
          // Best-effort: a dropped progress line never breaks the tool.
        }
        const trimmed = lastText.trim();
        if (trimmed) {
          recentNarration.push(trimmed);
        }
      }
    } finally {
      draining = false;
    }
  };

  // Thinking arrives as a high-frequency token stream (~40 chunks/s for a long
  // intent reasoning). Writing each chunk straight to React state forces one
  // transcript re-render per token, which saturates the main thread for the
  // whole build (the page appears frozen, the bubble can't keep up). Coalesce
  // the chunks: accumulate synchronously, but flush to React at most once per
  // THINKING_FLUSH_MS on a trailing timer so a burst of tokens collapses into a
  // single render.
  let thinkingFlushTimer: ReturnType<typeof setTimeout> | null = null;
  const flushThinking = (): void => {
    thinkingFlushTimer = null;
    if (closed || params.signal.aborted) return;
    params.onThinking(thinkingText);
  };

  const emit: ToolEmit = (event) => {
    if (params.signal.aborted || closed) return;
    if (event.tier === 'status') {
      params.setGenerationStatus(event.text);
      return;
    }
    if (event.tier === 'thinking') {
      thinkingText += event.textChunk;
      if (thinkingFlushTimer === null) {
        thinkingFlushTimer = setTimeout(flushThinking, THINKING_FLUSH_MS);
      }
      return;
    }
    queue.push({ kind: event.kind, digest: event.digest });
    void drain();
  };

  const close = (): void => {
    closed = true;
    queue.length = 0;
    if (thinkingFlushTimer !== null) {
      clearTimeout(thinkingFlushTimer);
      thinkingFlushTimer = null;
    }
    thinkingText = '';
  };

  return { emit, close };
}
